import React from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  subTitle?: string;
  className?: string;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  loading?: boolean;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, subTitle, leftContent, rightContent, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center animate-[fadeIn_0.2s_ease-out]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />

      {/* Modal */}
      <div className="relative rounded-[24px] h-[100vh] sm:h-auto sm:max-h-[90vh] overflow-hidden
        m-0 sm:m-[2vw] w-[100vw] sm:w-auto sm:min-w-[420px] sm:max-w-[480px]
        shadow-[0_8px_80px_rgba(0,134,211,0.25),0_2px_20px_rgba(0,0,0,0.4)]
        animate-[slideUp_0.3s_ease-out]"
      >
        {/* Background image */}
        <img
          src={require("../../assets/images/bannerBg.png")}
          alt=""
          className="absolute inset-0 w-full h-full object-cover scale-110"
        />

        {/* Glass overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#060e1f]/80 via-[#0a1628]/75 to-[#0d1f3c]/80 backdrop-blur-xl"></div>

        {/* Animated glow orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] left-[50%] translate-x-[-50%] w-[300px] h-[300px] bg-[#0086D3]/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[200px] h-[200px] bg-[#4DBDFF]/10 rounded-full blur-[80px]"></div>
          <div className="absolute bottom-[30%] right-[-10%] w-[150px] h-[150px] bg-[#0086D3]/8 rounded-full blur-[60px]"></div>
        </div>

        {/* Top accent line */}
        <div className="absolute top-0 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-[#4DBDFF]/40 to-transparent"></div>

        {/* Glass border */}
        <div className="absolute inset-0 rounded-[24px] border border-white/[0.08] pointer-events-none"></div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-[70px] sm:top-5 right-5 z-30 w-[34px] h-[34px] flex items-center justify-center
            rounded-full bg-white/[0.06] border border-white/[0.08]
            text-white/50 hover:text-white hover:bg-white/15 hover:border-white/20
            transition-all duration-300 hover:rotate-90"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative z-10 p-[24px] pt-[80px] sm:pt-[36px] sm:p-[36px] overflow-y-auto max-h-[100vh] sm:max-h-[90vh]">
          {loading ? (
            <div className="flex items-center justify-center h-[300px]">
              <div className="w-12 h-12 border-[3px] border-white/10 border-t-[#0086D3] rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {title && (
                <div className="mb-[20px]">
                  <h3 className="text-[30px] sm:text-[36px] font-extrabold text-white text-center tracking-tight">{title}</h3>
                  {subTitle && <p className="text-[14px] sm:text-[15px] text-white/45 text-center mt-[6px] font-medium tracking-wide">{subTitle}</p>}
                </div>
              )}
              <div>
                {children}
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Modal;
